import {
  endOfMonth,
  endOfWeek,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  LinearProgress,
  Container,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

import { useDashboardData } from '../../hooks/useDashboardData';
import KanbanBoard from '../../components/KanbanBoard';
import WeeklyChart from '../../components/WeeklyChart';
import EquipmentStatsChart from '../../components/EquipmentStatsChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export default function Dashboard() {
  const {
    estatisticas,
    statusFila,
    clientesNaoAtendidos,
    clientesAtendidos,
    clientesEmAtendimento,
    contagemAtivos,
    estatisticasEquipamentos,
    isLoading,
    hasError,
    recarregarDados,
  } = useDashboardData();
  const mesAtualAbreviado = new Date()
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '')
    .slice(0, 3)
    .toUpperCase();

  const calcularAtendidosSemanaPassada = () => {
    if (!Array.isArray(clientesAtendidos) || clientesAtendidos.length === 0) {
      return 0;
    }

    const hoje = new Date();
    const inicioSemanaAtual = startOfWeek(hoje, { weekStartsOn: 1 });
    const inicioSemanaPassada = subWeeks(inicioSemanaAtual, 1);
    const fimSemanaPassada = endOfWeek(inicioSemanaPassada, { weekStartsOn: 1 });

    return clientesAtendidos.filter((item) => {
      const data = new Date(item?.dataFim || item?.fimAtendimento || '');
      if (Number.isNaN(data.getTime())) return false;

      return isWithinInterval(data, {
        start: inicioSemanaPassada,
        end: fimSemanaPassada,
      });
    }).length;
  };

  const calcularNaoAtendidosNoMes = () => {
    if (!Array.isArray(clientesNaoAtendidos) || clientesNaoAtendidos.length === 0) {
      return 0;
    }

    const hoje = new Date();
    const inicioMes = startOfMonth(hoje);
    const fimMes = endOfMonth(hoje);

    return clientesNaoAtendidos.filter((item) => {
      const rawDate = item?.timestamp || item?.dataSaida || item?.createdAt || '';
      const data = new Date(rawDate);
      if (Number.isNaN(data.getTime())) return false;

      return isWithinInterval(data, {
        start: inicioMes,
        end: fimMes,
      });
    }).length;
  };

  const getStatsData = () => {
    if (isLoading) {
      return [
        {
          title: 'Em atendimento',
          value: '...',
          icon: PeopleIcon,
          color: 'success',
        },
        {
          title: 'Atendidos semana passada',
          value: '...',
          icon: AssignmentIcon,
          color: 'info',
        },
        {
          title: `Atendidos ${mesAtualAbreviado}`,
          value: '...',
          icon: AssignmentIcon,
          color: 'warning',
          progress: 0,
        },
        {
          title: `Não atendidos ${mesAtualAbreviado}`,
          value: '...',
          icon: AssignmentIcon,
          color: 'error',
        },
      ];
    }

    return [
      {
        title: 'Em atendimento',
        value: contagemAtivos ?? '0',
        icon: PeopleIcon,
        color: 'success',
      },
      {
        title: 'Atendidos semana passada',
        value: calcularAtendidosSemanaPassada().toString(),
        icon: AssignmentIcon,
        color: 'info',
      },
      {
        title: `Atendidos ${mesAtualAbreviado}`,
        value: estatisticas?.atendidosNoMes || clientesAtendidos?.length || '0',
        icon: AssignmentIcon,
        color: 'warning',
        progress: estatisticas?.progressoMes || 75,
      },
      {
        title: `Não atendidos ${mesAtualAbreviado}`,
        value: calcularNaoAtendidosNoMes().toString(),
        icon: AssignmentIcon,
        color: 'error',
      },
    ];
  };

  const stats = getStatsData();

  const renderStatCard = (stat, statIndex) => {
    const IconComponent = stat.icon;
    return (
      <Grid item xs={12} sm={6} lg={3} key={statIndex}>
        <Card
          sx={{
            height: '100%',
            backgroundColor: '#1e293b',
            color: 'white',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: 'none',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Stack
                direction="row"
                sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}
                spacing={2}
              >
                <Stack spacing={1.5}>
                  <Typography
                    color="rgba(255, 255, 255, 0.6)"
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography
                    variant="h3"
                    color="white"
                    sx={{ fontWeight: 700, lineHeight: 1 }}
                  >
                    {stat.value}
                  </Typography>
                </Stack>
                <Avatar
                  sx={{
                    backgroundColor: `${stat.color}.main`,
                    height: 48,
                    width: 48,
                    boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <IconComponent sx={{ fontSize: 24 }} />
                </Avatar>
              </Stack>
              {stat.progress !== undefined && (
                <Box>
                  <LinearProgress
                    value={stat.progress}
                    variant="determinate"
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: `${stat.color}.main`,
                      },
                    }}
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (isLoading && !estatisticas && !statusFila) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5, color: 'white' }}>
          Dashboard
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (hasError && !estatisticas && !statusFila) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5, color: 'white' }}>
          Dashboard
        </Typography>
        <Alert
          severity="error"
          action={
            <button type="button" onClick={recarregarDados}>
              Tentar novamente
            </button>
          }
        >
          Erro ao carregar dados do dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        {stats.map((stat, statIndex) => renderStatCard(stat, statIndex))}

        <Grid item xs={12} container spacing={4}>
          <Grid item xs={12} md={8}>
            <WeeklyChart atendidos={clientesAtendidos} naoAtendidos={clientesNaoAtendidos} />
          </Grid>
          <Grid item xs={12} md={4}>
            <EquipmentStatsChart
              isLoading={isLoading}
              estatisticasEquipamentos={estatisticasEquipamentos}
              clientesAtendidos={clientesAtendidos}
            />
          </Grid>
        </Grid>

        <KanbanBoard
          isLoading={isLoading}
          statusFila={statusFila}
          clientesAtendidos={clientesAtendidos}
          clientesNaoAtendidos={clientesNaoAtendidos}
          clientesEmAtendimento={clientesEmAtendimento}
        />
      </Grid>
    </Container>
  );
}
